import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Upload, Save, RotateCcw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

export default function ThemeBuilder() {
  const { user } = useAuth();
  const [theme, setTheme] = useState({
    logo_url: null,
    primary_color: '#1976D2',
    secondary_color: '#00897B',
    accent_color: '#FF6D00',
    company_name: 'My Tax Agency',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      fetchTheme();
    }
  }, [user]);

  const fetchTheme = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${backendUrl}/api/agency/theme`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTheme({
        logo_url: response.data.logo_url,
        primary_color: response.data.primary_color,
        secondary_color: response.data.secondary_color,
        accent_color: response.data.accent_color,
        company_name: response.data.company_name
      });
    } catch (error) {
      console.error('Error fetching theme:', error);
      toast.error('Failed to load theme');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');

      await axios.put(`${backendUrl}/api/agency/theme`, theme, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Theme saved successfully!');
    } catch (error) {
      console.error('Error saving theme:', error);
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTheme({
      logo_url: null,
      primary_color: '#1976D2',
      secondary_color: '#00897B',
      accent_color: '#FF6D00',
      company_name: 'My Tax Agency',
    });
    toast.info('Theme reset to default');
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file size must be less than 2MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        // Upload logo to backend
        const backendUrl = getBackendUrl();
        const token = localStorage.getItem('access_token');

        const response = await axios.post(
          `${backendUrl}/api/agency/upload-logo`,
          { logo: base64 },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setTheme({ ...theme, logo_url: response.data.logo_url });
        toast.success('Logo uploaded successfully!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    }
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Theme Builder" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="agency" title="Theme Builder" user={{ ...user, role: 'Agency Admin' }}>
      <div className="mx-auto max-w-5xl space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Theme Builder</h2>
          <p className="mt-1 text-muted-foreground">
            Customize the platform with your brand identity
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Theme Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Logo */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Brand Logo</CardTitle>
                <CardDescription>Upload your company logo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed bg-muted">
                    {theme.logo_url ? (
                      <img src={theme.logo_url} alt="Logo" className="h-full w-full object-contain" />
                    ) : (
                      <Upload className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-2">
                      Recommended size: 200x200px, PNG or SVG format
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => document.getElementById('logo-upload').click()}
                    >
                      <Upload className="h-4 w-4" />
                      Upload Logo
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Info */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={theme.company_name}
                    onChange={(e) => setTheme({ ...theme, company_name: e.target.value })}
                    placeholder="Enter company name"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Color Scheme */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Color Scheme</CardTitle>
                <CardDescription>Choose your brand colors</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="primaryColor">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="primaryColor"
                        type="color"
                        value={theme.primary_color}
                        onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })}
                        className="h-10 w-16 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={theme.primary_color}
                        onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondaryColor">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="secondaryColor"
                        type="color"
                        value={theme.secondary_color}
                        onChange={(e) => setTheme({ ...theme, secondary_color: e.target.value })}
                        className="h-10 w-16 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={theme.secondary_color}
                        onChange={(e) => setTheme({ ...theme, secondary_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accentColor">Accent Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="accentColor"
                        type="color"
                        value={theme.accent_color}
                        onChange={(e) => setTheme({ ...theme, accent_color: e.target.value })}
                        className="h-10 w-16 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={theme.accent_color}
                        onChange={(e) => setTheme({ ...theme, accent_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview */}
          <div className="lg:col-span-1">
            <Card className="card-elevated sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Live Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 rounded-lg border p-4">
                  {/* Preview Header */}
                  <div className="flex items-center gap-2 pb-3 border-b">
                    <div
                      className="h-8 w-8 rounded flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: theme.primary_color }}
                    >
                      {theme.company_name.charAt(0)}
                    </div>
                    <span className="font-semibold text-sm">{theme.company_name}</span>
                  </div>

                  {/* Preview Button */}
                  <button
                    className="w-full rounded-md px-4 py-2 text-sm font-medium text-white transition-colors"
                    style={{ backgroundColor: theme.primary_color }}
                  >
                    Primary Button
                  </button>

                  {/* Preview Card */}
                  <div className="rounded-lg border p-3 space-y-2">
                    <div
                      className="h-2 w-16 rounded"
                      style={{ backgroundColor: theme.secondary_color }}
                    />
                    <div className="h-2 w-full rounded bg-muted" />
                    <div className="h-2 w-3/4 rounded bg-muted" />
                  </div>

                  {/* Preview Badge */}
                  <div className="flex gap-2">
                    <span
                      className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium text-white"
                      style={{ backgroundColor: theme.accent_color }}
                    >
                      Accent Badge
                    </span>
                  </div>
                </div>

                <Separator className="my-4" />

                <p className="text-xs text-muted-foreground">
                  This preview shows how your theme will appear in the end-user portal
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleReset} disabled={saving} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Reset to Default
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Theme'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
